import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import Loader from './Loader';
import { TermsModal } from './TermsModal';
import { useSidebar, useHubspot } from '../hooks';
import { getSidebarConfig } from '../utils';
import { useAuth } from '../contexts/AuthContext';
import { HubspotUser } from '../types/hubspot';
import usersService from '../services/usersService';
import { useToast } from '../hooks';
import { EnterpriseProvider, useEnterprise, ViewType } from '../contexts/EnterpriseContext';

import { ROLES } from '../constants/roles';

/**
 * Globally syncs the ?view= query param with viewType in EnterpriseContext
 * for all /travel-managers routes on page load and navigation.
 *
 * Rule:
 *  - ?view= EXISTS in URL → update state to match it (URL is source of truth).
 *  - ?view= ABSENT        → stamp current state value into URL (preserves the
 *                           chosen view when navigating between pages).
 *
 * User-driven toggles (setViewType called from a component) are responsible for
 * also updating the URL at the call site — see handleViewToggle in ProgramSummary.
 */
const ViewTypeSync: React.FC = () => {
  const { viewType, setViewType } = useEnterprise();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  //do not show the param for the reporting tab of enterprise admin (travel manager routes = enterprise admin)
  const isTravelManagerRoute = location.pathname.startsWith('/travel-managers') && !location.pathname.includes('/reporting');

  useEffect(() => {
    if (!isTravelManagerRoute) return;
    const paramView = searchParams.get('view');
    if (paramView === 'fasttrack' || paramView === 'full') {
      // URL has a valid value → sync state only, never touch the URL
      if (paramView !== viewType) {
        setViewType(paramView as ViewType);
      }
    } else {
      // No param → stamp current state into URL (keeps fasttrack across pages)
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('view', viewType);
          return next;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
};

const DashboardLayout: React.FC = () => {
  const {
    loading,
    tabsLoading,
    user,
    isSuperAdmin,
    isIntegrationsAdmin,
    isBrandAdmin,
    handleSetAgreedToTerms,
    refreshUserProfile,
  } = useAuth();
  const { success, error } = useToast();
  const { isOpen: sidebarOpen, toggle: toggleSidebar } = useSidebar(true);

  // Terms modal state - only show once per session if user hasn't agreed
  const [shouldShowTermsModal, setShouldShowTermsModal] = useState(false);

  useEffect(() => {
    refreshUserProfile();
  }, []);

  // Check if user needs to agree to terms
  useEffect(() => {
    if (!user) return;

    const termsCheckedKey = `termsChecked_${user.id}`;
    const hasCheckedInSession = sessionStorage.getItem(termsCheckedKey);



    // Only show modal if:
    // 1. User hasn't agreed to terms (agreedToTerms is false)
    // 2. We haven't already checked in this session
    // !isSuperAdmin &&  !user.agreedToTerms && !hasCheckedInSession
    if (
      !isSuperAdmin &&
      !user.agreedToTerms &&
      !hasCheckedInSession &&
      !isIntegrationsAdmin &&
      !isBrandAdmin
    ) {
      setShouldShowTermsModal(true);
      // Mark as checked in this session
    }
    sessionStorage.setItem(termsCheckedKey, 'true');
  }, [user, isSuperAdmin, isIntegrationsAdmin, isBrandAdmin]);

  // Get sidebar configuration (you can change 'default' to 'dark', 'minimal', or 'wide')
  const sidebarConfig = getSidebarConfig('default');

  // Initialize HubSpot for authenticated users
  const intercomUser: HubspotUser | null = user
    ? {
        user_id: user.id,
        name: user.name,
        email: user.email,
        created_at: user.lastLogin
          ? Math.floor(new Date(user.lastLogin).getTime() / 1000)
          : undefined,
        role: user.role,
      }
    : null;

  // Don't show HubSpot widget for enterprise admin users
  const shouldEnableIntercom =
    !!user &&
    user.role !== ROLES.ENTERPRISE_ADMIN &&
    !isIntegrationsAdmin &&
    !isBrandAdmin;


  useHubspot({
    user: intercomUser,
    enabled: shouldEnableIntercom,
  });

  const handleSidebarLinkClick = (path: string) => {
    // Handle mobile sidebar closing on link click
    if (window.innerWidth <= 768) {
      // Close sidebar on mobile after navigation
      console.log(`Navigating to: ${path}`);
    }
  };

  // Handle terms agreement
  const handleAgreeTerms = async () => {
    try {
      await usersService.agreeToTerms();
      success('Terms and conditions accepted successfully');

      // Refresh user profile to update agreedToTerms status
      handleSetAgreedToTerms(true);

      // Clear session storage since user has now agreed
      if (user) {
        sessionStorage.removeItem(`termsChecked_${user.id}`);
      }

      setShouldShowTermsModal(false);
    } catch (err: any) {
      error(err.message || 'Failed to agree to terms. Please try again.');
      console.error('Error agreeing to terms:', err);
    }
  };

  // Handle terms cancellation
  const handleCancelTerms = async () => {
    // No API call for disagree yet, just close the modal
    console.log('User cancelled terms agreement');
    setShouldShowTermsModal(false);
  };

  if (tabsLoading) {
    return <Loader fullScreen />;
  }

  return (
    <EnterpriseProvider>
      <ViewTypeSync />
        <div className="flex h-screen bg-[var(--color-bg-1`)]">
          <Sidebar
            isOpen={sidebarOpen}
            onLinkClick={handleSidebarLinkClick}
            {...sidebarConfig}
          />
          <div className="flex-1 flex flex-col min-w-0 h-full">
            <TopBar onToggleSidebar={toggleSidebar} />
            <main
              className="flex-1 overflow-auto custom-scrollbar"
              data-scroll-container
              style={{
                scrollbarGutter: 'stable',
              }}
            >
              <Outlet />
            </main>
          </div>


        </div>

        {/* Terms and Conditions Modal */}
        <TermsModal
          isOpen={shouldShowTermsModal}
          onAgree={handleAgreeTerms}
          onCancel={handleCancelTerms}
          onClose={() => setShouldShowTermsModal(false)}
        />
    </EnterpriseProvider>
  );
};

export default DashboardLayout;
